/*
 * View3DScreen.jsx — ARGO · 3D Viewer (đổi tên từ DetachScreen)
 */
import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader }    from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import styled, { keyframes } from 'styled-components';

const BLUE = '#00C2FF';

const fadeIn  = keyframes`from{opacity:0}to{opacity:1}`;
const spin    = keyframes`to{transform:rotate(360deg)}`;

const Container = styled.div`position:fixed;inset:0;background:#0c1018;z-index:100;animation:${fadeIn} .3s ease both;`;
const Stage     = styled.div`position:absolute;inset:0;canvas{display:block;touch-action:none;}`;
const TopBar    = styled.div`position:absolute;top:0;left:0;right:0;z-index:8;padding:max(16px,env(safe-area-inset-top)) 16px 0;background:linear-gradient(to bottom,rgba(7,9,13,.85),transparent);pointer-events:none;`;
const BackBtn   = styled.button`display:inline-flex;align-items:center;gap:7px;padding:9px 16px;border-radius:12px;border:0.5px solid rgba(255,255,255,.18);background:rgba(8,27,58,.7);backdrop-filter:blur(12px);color:#fff;cursor:pointer;font-family:'Exo 2',sans-serif;font-weight:600;font-size:13px;pointer-events:auto;transition:background .15s;&:active{background:rgba(0,194,255,.2);}`;
const Title     = styled.div`font-family:'Exo 2',sans-serif;font-weight:600;font-size:15px;color:#fff;margin-top:8px;`;
const Hint      = styled.div`font-size:11px;color:rgba(255,255,255,.4);margin-top:3px;`;
const Loader    = styled.div`position:absolute;inset:0;z-index:10;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;background:#0c1018;`;
const Spinner   = styled.div`width:36px;height:36px;border-radius:50%;border:2.5px solid rgba(0,194,255,.2);border-top-color:${BLUE};animation:${spin} .8s linear infinite;`;

const loader = new GLTFLoader();

export default function View3DScreen({ product, onBack }) {
  const stageRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    const container = stageRef.current;
    if (!container || !product?.modelUrl) return;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);

    const scene  = new THREE.Scene();
    scene.background = new THREE.Color(0x0c1018);

    const camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.01, 100);
    camera.position.set(0, 0.3, 1.2);

    scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const dir = new THREE.DirectionalLight(0xffffff, 1.4); dir.position.set(2,4,3); scene.add(dir);
    const fill = new THREE.DirectionalLight(0x00c8bd, 0.4); fill.position.set(-2,-1,2); scene.add(fill);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true; controls.dampingFactor = 0.08;
    controls.minDistance = 0.3; controls.maxDistance = 4.0;
    controls.update();

    let mixer = null;
    loader.load(product.modelUrl, (gltf) => {
      const model = gltf.scene;
      model.scale.setScalar(product.modelScale ?? 0.08);
      const box = new THREE.Box3().setFromObject(model);
      model.position.sub(box.getCenter(new THREE.Vector3()));
      scene.add(model);

      const size = box.getSize(new THREE.Vector3()).length();
      camera.position.set(0, size * 0.3, size * 1.2);
      controls.update();

      if (gltf.animations.length > 0) {
        mixer = new THREE.AnimationMixer(model);
        const clips = {};
        gltf.animations.forEach(c => { clips[c.name] = c; });
        const { open, idle } = product.animations ?? {};
        const openClip = (open && clips[open]) || Object.values(clips)[0];
        if (openClip) {
          const action = mixer.clipAction(openClip);
          action.setLoop(THREE.LoopOnce, 1); action.clampWhenFinished = true; action.play();
          if (idle && clips[idle]) {
            mixer.addEventListener('finished', (e) => {
              if (e.action !== action) return;
              mixer.clipAction(clips[idle]).setLoop(THREE.LoopRepeat).reset().play();
            });
          }
        }
      }
      setLoading(false);
    }, undefined, (err) => { console.error(err); setError('Không thể tải mô hình 3D'); setLoading(false); });

    const ro = new ResizeObserver(() => {
      const w = container.clientWidth, h = container.clientHeight;
      camera.aspect = w / h; camera.updateProjectionMatrix(); renderer.setSize(w, h);
    });
    ro.observe(container);

    const clock = new THREE.Clock();
    renderer.setAnimationLoop(() => {
      const dt = clock.getDelta();
      if (mixer) mixer.update(dt);
      controls.update();
      renderer.render(scene, camera);
    });

    return () => {
      ro.disconnect();
      renderer.setAnimationLoop(null);
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, []); /* eslint-disable-line */

  return (
    <Container>
      <Stage ref={stageRef} />
      {loading && <Loader><Spinner /><p style={{fontSize:13,color:'rgba(255,255,255,.5)',fontFamily:'monospace'}}>Đang tải mô hình 3D…</p></Loader>}
      {error   && <Loader><p style={{color:'rgba(255,255,255,.6)',fontSize:14}}>{error}</p><button onClick={onBack} style={{padding:'10px 20px',borderRadius:12,border:`0.5px solid ${BLUE}`,background:'transparent',color:'#fff',cursor:'pointer',fontSize:14}}>Quay lại</button></Loader>}
      {!loading && !error && (
        <TopBar>
          <BackBtn onClick={onBack}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            Quay lại
          </BackBtn>
          {product?.info?.title && <Title>{product.info.title}</Title>}
          <Hint>Kéo để xoay · Chụm để zoom</Hint>
        </TopBar>
      )}
    </Container>
  );
}
